/**
 * The event dock's priority rule, as a pure function.
 *
 * NOTE the file name: this must NOT be `eventDock.ts` next to `EventDock.tsx`.
 * macOS is case-insensitive, so `./eventDock` resolved to the COMPONENT and
 * the dock silently imported itself (`pickDockEvent` came out undefined).
 *
 * The dock is a FIXED-HEIGHT slot: it is always rendered (transparent while
 * empty) and shows at most ONE event. That is the whole point — the old
 * "stack the cards above the MoveList" layout resized the notation every time
 * a draw offer arrived, which moved the moves under the user's cursor and
 * threw away the scroll position.
 *
 * THE FIRST-MOVE COUNTDOWN IS NOT A DOCK EVENT any more (decision D2). The chip
 * was anonymous and, worse, keyed on "I still owe A first move" rather than "I
 * am the side to move": black saw it before white had moved, counting down
 * WHITE's deadline. It is now a banner on the owing player's row —
 * `stage/PlayerRow/FirstMoveBanner.*`, the app's shape — which is also where
 * the old screen put it (`PlayerComponent` L110-120).
 */
/**
 * The dock slot's height, in ONE place (unit convention §2).
 *
 * 40 since the clean-up of 2026-10-05 (R6.4) shrank it from the 88 px card
 * M1a drew; fix G (G6, 2026-10-06) grew it back to 88 for ONE reason: the
 * draw-offer card's text was truncating next to its two buttons at 40 px, and
 * the fix is to give the text its own row above a right-aligned button row
 * (`EventDock/cards/DrawOfferCard.View.tsx`) — which a single-row 40 px slot
 * cannot hold. The slot is a FIXED height regardless of which event (or none)
 * is showing, so every card pays for the tallest one; `ThinkingCard` /
 * `NoticeLine` simply centre in more room now (`EventDock/View.tsx`'s own
 * `justifyContent: 'center'`). It is a local constant rather than
 * `SIZE.dock` because `theme/tokens.ts` is the shared core
 * (`src/Store/liveGame/SYNC.md`) and the app lane still draws its own 88 px
 * card unchanged — this is coincidentally the SAME number, not a reach back
 * into that token (D-x4's precedent: a local View constant, not a shared one).
 */
export const DOCK_HEIGHT = 88;

/** how long the "declined" notice stays after draw_reject (UX spec §3) */
export const DRAW_DECLINED_MS = 2000;

/**
 * The dock's inputs. It is `selectDockInput`'s shape MINUS the first-move
 * fields, which the banner reads from its own selector: an object with extra
 * keys still satisfies this type, so the core selector did not have to change.
 */
export type DockInput = {
  drawOffered: boolean;
  drawRejectedAt: number | null;
  thinkingGateUntil: number | null;
  opponentDisconnected: boolean;
  opponentName: string;
};

export type DockEvent =
  | {kind: 'drawOffered'; opponentName: string}
  | {kind: 'thinking'; until: number}
  | {kind: 'disconnected'; opponentName: string}
  | {kind: 'drawDeclined'; at: number; opponentName: string};

/**
 * "a draw offer is pending for me" — `pickDockEvent`'s own top-priority
 * check, exported so `rail/live/FloatingDrawOffer` (fix S / S6) can ask the
 * SAME question without growing a second rule.
 *
 * T0.2 (Fix S review #2): typed on a `Pick`, not the full `DockInput` — the
 * body only ever reads `drawOffered`, and `FloatingDrawOffer` now passes its
 * own narrowed selector's result (not the full `selectDockInput`, which
 * carries `moves` and re-rendered the floater on every opponent move). Every
 * existing caller already satisfies a `Pick` of the object it builds.
 */
export const isDrawOfferPending = (
  input: Pick<DockInput, 'drawOffered'>,
): boolean => input.drawOffered;

/**
 * Priority (highest first): an offer I must answer, a gate that has locked my
 * board, the opponent dropping, the 2 s "declined" notice. Anything that needs
 * an answer outranks anything informational.
 */
export function pickDockEvent(
  input: DockInput,
  now: number = Date.now(),
): DockEvent | null {
  if (input.drawOffered) {
    return {kind: 'drawOffered', opponentName: input.opponentName};
  }
  if (input.thinkingGateUntil && input.thinkingGateUntil > now) {
    return {kind: 'thinking', until: input.thinkingGateUntil};
  }
  if (input.opponentDisconnected) {
    return {kind: 'disconnected', opponentName: input.opponentName};
  }
  if (input.drawRejectedAt && now - input.drawRejectedAt < DRAW_DECLINED_MS) {
    return {
      kind: 'drawDeclined',
      at: input.drawRejectedAt,
      opponentName: input.opponentName,
    };
  }
  return null;
}
